import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Facebook, Instagram, MessageCircle, Mail } from "lucide-react";

const safariStyles = `
  .gradient-text {
    background: linear-gradient(90deg, #6ee7ff, #b26eff, #47ffa1);
    background: -webkit-linear-gradient(90deg, #6ee7ff, #b26eff, #47ffa1);
    background: -moz-linear-gradient(90deg, #6ee7ff, #b26eff, #47ffa1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
  
  @supports not (-webkit-background-clip: text) {
    .gradient-text {
      background: none;
      color: #6ee7ff;
    }
  }
`;

export default function CreationInnovation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const beamTranslate = useTransform(scrollYProgress, [0, 1], [0, -80]);

  useEffect(() => {
    // Inject Safari gradient styles
    const styleElement = document.createElement('style');
    styleElement.textContent = safariStyles;
    document.head.appendChild(styleElement);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrame: number;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const animate = () => {
      time += 0.01;

      const gradient = ctx.createRadialGradient(
        canvas.width * 0.3 + Math.sin(time) * 200,
        canvas.height * 0.4 + Math.cos(time * 0.8) * 150,
        0,
        canvas.width * 0.7,
        canvas.height * 0.6,
        canvas.width * 0.6
      );

      gradient.addColorStop(0, `hsla(${190 + Math.sin(time) * 20}, 80%, 70%, 0.1)`);
      gradient.addColorStop(0.5, `hsla(${280 + Math.cos(time * 0.7) * 30}, 70%, 65%, 0.08)`);
      gradient.addColorStop(1, "transparent");

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      animationFrame = requestAnimationFrame(animate);
    };

    resize();
    animate();

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrame);
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="creation-innovation"
      className="relative min-h-screen overflow-hidden text-foreground"
    >
      {/* Animated Background Canvas (match hero) */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ mixBlendMode: "multiply" }}
      />

      {/* Parallax light beams (match hero accents) */}
      <motion.div className="absolute inset-0 opacity-30" style={{ y: beamTranslate }} aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-1 h-96 bg-gradient-to-b from-accent to-transparent rotate-12 blur-sm" />
        <div className="absolute top-1/3 right-1/3 w-1 h-80 bg-gradient-to-b from-accent2 to-transparent -rotate-6 blur-sm" />
      </motion.div>



      <div className="relative z-10 max-w-6xl mx-auto h-full px-6 md:px-10 flex flex-col pb-40 md:pb-0">
        {/* Heading */}
        <header className="pt-10 md:pt-14">
          <h2 className="leading-tight tracking-tight">
            <span className="block text-[38px] md:text-[54px] font-semibold gradient-text animate-pulse">CREATION &</span>
            <span className="block text-[42px] md:text-[64px] font-light">INNOVATION</span>
          </h2>
        </header>

        {/* Content with left vertical rule and numbered sections */}
        <div className="relative flex-1 mt-6 md:mt-8">
          {/* Vertical divider */}
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-foreground/20" />

          <div className="pl-10 md:pl-14 max-w-3xl space-y-10 md:space-y-12">
            {/* 01 */}
            <div className="flex items-start gap-6">
              <div className="shrink-0 text-2xl md:text-3xl font-semibold tracking-wider text-foreground/70">01</div>
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-semibold">Background</h3>
                <p className="text-sm md:text-[15px] leading-relaxed text-foreground/80">
                  Holding a Master’s degree in Business Law while also creating websites for large international companies. This unique combination of skills allowed me to bridge the gap between legal intricacies and digital innovation. When faced with a challenge from a client who was struggling with compliance issues on their e‑commerce platform, I was able to leverage my expertise to develop a solution that not only ensured legal compliance but also enhanced the user experience.
                </p>
              </div>
            </div>

            {/* 02 */}
            <div className="flex items-start gap-6">
              <div className="shrink-0 text-2xl md:text-3xl font-semibold tracking-wider text-foreground/70">02</div>
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-semibold">Web Development</h3>
                <ul className="list-disc pl-5 text-sm md:text-[15px] space-y-1 text-foreground/80">
                  <li><span className="font-semibold">Languages & Frameworks:</span> PHP, JavaScript, HTML5, CSS3, Bootstrap, Tailwind CSS</li>
                  <li><span className="font-semibold">CMS Platforms:</span> WordPress, Webflow, Wix, Shopify, Squarespace, Joomla</li>
                  <li><span className="font-semibold">eCommerce & Page Builders:</span> Shopify Tools: Replo, GemPages, PageFly, Shogun, Zipify Pages, EComposer</li>
                  <li><span className="font-semibold">WooCommerce Builders:</span> Elementor, WPBakery, Divi, Bricks, Oxygen Builder</li>
                </ul>
              </div>
            </div>

            {/* 03 */}
            <div className="flex items-start gap-6">
              <div className="shrink-0 text-2xl md:text-3xl font-semibold tracking-wider text-foreground/70">03</div>
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-semibold">Mobile App Development</h3>
                <ul className="list-disc pl-5 text-sm md:text-[15px] space-y-1 text-foreground/80">
                  <li><span className="font-semibold">Cross-Platform:</span> Flutter (iOS & Android), React Native</li>
                  <li><span className="font-semibold">Native Development:</span> Swift (iOS)</li>
                  <li><span className="font-semibold">API Integration:</span> RESTful APIs, Firebase, Payment Gateways (Stripe, PayPal, etc.)</li>
                </ul>
              </div>
            </div>

            {/* 04 */}
            <div className="flex items-start gap-6">
              <div className="shrink-0 text-2xl md:text-3xl font-semibold tracking-wider text-foreground/70">04</div>
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-semibold">Tools & Workflows</h3>
                <ul className="list-disc pl-5 text-sm md:text-[15px] space-y-1 text-foreground/80">
                  <li><span className="font-semibold">Version Control:</span> Git, GitHub, Bitbucket</li>
                  <li><span className="font-semibold">Design & Prototyping:</span> Figma, Adobe XD, Canva</li>
                  <li><span className="font-semibold">Hosting & Deployment:</span> cPanel, FTP, Firebase Hosting, Netlify, Vercel</li>
                  <li><span className="font-semibold">SEO Optimization:</span> On-Page SEO, Schema Markup, Yoast SEO, RankMath</li>
                </ul>
                {/* Minimalistic contact buttons */}
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    href="https://www.fb.com/shotaos"
                    target="_blank"
                    rel="noreferrer"
                    className="glass neon-hover inline-flex items-center gap-2 px-3 py-2 text-xs md:text-sm text-foreground/80"
                  >
                    <Facebook className="w-4 h-4 text-accent" />
                    <span>Facebook</span>
                  </a>
                  <a
                    href="https://instagram.com/ishotiko"
                    target="_blank"
                    rel="noreferrer"
                    className="glass neon-hover inline-flex items-center gap-2 px-3 py-2 text-xs md:text-sm text-foreground/80"
                  >
                    <Instagram className="w-4 h-4 text-accent2" />
                    <span>@ishotiko</span>
                  </a>
                  <a
                    href="https://wa.me/995574355888"
                    target="_blank"
                    rel="noreferrer"
                    className="glass neon-hover inline-flex items-center gap-2 px-3 py-2 text-xs md:text-sm text-foreground/80"
                  >
                    <MessageCircle className="w-4 h-4 text-accent3" />
                    <span>WhatsApp</span>
                  </a>
                  <a
                    href="mailto:info@shota.in"
                    className="glass neon-hover inline-flex items-center gap-2 px-3 py-2 text-xs md:text-sm text-foreground/80"
                  >
                    <Mail className="w-4 h-4 text-foreground/70" />
                    <span>info@shota.in</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile image anchored to section bottom-right (unchanged) */}
      <div className="pointer-events-none select-none absolute right-6 md:right-10 bottom-0 z-10">
        <img
          src="/shota.png"
          alt="Profile"
          className="block w-64 h-64 md:w-[22rem] md:h-[22rem] lg:w-[28rem] lg:h-[28rem] object-contain object-bottom"
          loading="eager"
        />
      </div>

      {/* Floating accent dots (subtle) */}
      <motion.div
        className="absolute top-1/5 left-8 w-3 h-3 bg-accent rounded-full blur-sm opacity-60"
        animate={{ y: [0, -20, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
      />
      <motion.div
        className="absolute top-2/3 right-12 w-2 h-2 bg-accent2 rounded-full blur-sm opacity-50"
        animate={{ y: [0, 15, 0], x: [0, -10, 0], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
      />
    </section>
  );
}